/**
 * A service class for handling multipart audio uploads to an AWS S3 bucket.
 *
 * This class provides functionality to start a multipart upload, upload parts of an audio file sequentially,
 * and complete the multipart upload process. It's particularly useful for large audio files that need to be
 * uploaded in smaller chunks.
 *
 * @class
 */
export class AudioUploadService {
  /**
   * Creates an AudioUploadService instance.
   * @param {AWS.S3} s3 - An instance of the AWS.S3 class, which is used to communicate with the S3 service.
   * @param {string} bucket - The name of the S3 bucket to which the audio file will be uploaded.
   * @param {string} key - The key (path) of the file in the S3 bucket.
   * @constructor
   * @public
   * @memberof AudioUploadService
   * @example
   * const s3 = new AWS.S3();
   * const bucket = "my-bucket";
   * const key = "path/to/file.mp3";
   * const audioUploadService = new AudioUploadService(s3, bucket, key);
   * audioUploadService.startMultipartUpload((err, data) => {
   *  if (err) {
   *   console.log("Error starting multipart upload", err);
   *  return;
   * }
   * console.log("Multipart upload started", data);
   * });
   */

  constructor(s3, bucket, key) {
    this.s3 = s3;
    this.bucket = bucket;
    this.key = key;
    this.uploadId = null;
    this.parts = [];
  }

  /**
   * Initiates a multipart upload to an S3 bucket. This asynchronous function starts the
   * process of uploading a file in parts, which is useful for large files.
   *
   * @param {Function} callback - A callback function that is called upon completion of
   * the multipart upload initiation. The callback receives two arguments:
   *   - error: An error object if the operation fails, or null if successful.
   *   - data: The data from the S3 service indicating the successful initiation of the upload.
   *     Includes properties like 'UploadId'.
   *
   * The function sets the 'uploadId' property of the class instance with the ID returned
   * by the S3 service, which is used for subsequent upload parts.
   *
   * @async
   */
  async startMultipartUpload(callback) {
    const params = {
      Bucket: this.bucket,
      Key: this.key,
      ContentType: "audio/webm"
    };
    if (this.uploadId) return;

    this.s3.createMultipartUpload(params, (err, data) => {
      if (err) {
        if (callback) callback(err, null);
        return;
      }
      this.uploadId = data.UploadId;
      if (callback) callback(null, data);
    });
  }

  /**
   * Uploads a part of the audio file to an AWS S3 bucket as part of a multipart upload.
   *
   * This method should be called only after initiating a multipart upload using startMultipartUpload.
   * Each part of the file is uploaded sequentially, and this method keeps track of each part's
   * ETag and PartNumber, which are required to complete the multipart upload.
   *
   * @param {Blob} partData - The part of the file to be uploaded. This should be a Blob object containing the audio data.
   * @param {number} partNumber - The part number of this upload. This is used to identify the order of the parts in the final assembly of the file.
   * @param {Function} [callback] - An optional callback function that is called upon completion of the part upload. The callback receives two arguments:
   *                                - error: An error object if the operation fails, or null if successful.
   *                                - data: The data from the S3 service indicating the successful upload of the part. Includes properties like 'ETag'.
   *
   * If the uploadId is not set (i.e., if startMultipartUpload has not been called), the callback is invoked with an error.
   *
   * @async
   */

  async uploadPart(partData, partNumber, callback) {
    if (!this.uploadId) {
      if (callback) callback("Upload ID is not set", null);
      return;
    }

    const params = {
      Bucket: this.bucket,
      Key: this.key,
      PartNumber: partNumber,
      UploadId: this.uploadId,
      Body: partData
    };
    this.s3.uploadPart(params, (err, data) => {
      if (err) {
        if (callback) callback(err, null);
        return;
      }
      this.parts.push({
        ETag: data.ETag,
        PartNumber: partNumber
      });
      if (callback) callback(null, data);
    });
  }

  /**
   * Completes a multipart upload to an AWS S3 bucket.
   *
   * This method is called to finalize a multipart upload after all parts of the file have been uploaded.
   * It assembles the previously uploaded parts (based on their PartNumber) into a single file in the S3 bucket.
   *
   * @param {Function} [callback] - An optional callback function that is called upon completion of the multipart upload completion. The callback receives two arguments:
   *                                - error: An error object if the operation fails, or null if successful.
   *                                - data: The data from the S3 service indicating the successful completion of the upload. Includes details of the uploaded file.
   *
   * If the uploadId is not set (i.e., if startMultipartUpload has not been called or if no parts have been uploaded), the callback is invoked with an error.
   *
   * @async
   */
  async completeMultipartUpload(body, callback) {
    if (!this.uploadId) {
      if (callback) callback("Upload ID is not set", null);
      return;
    }
    // If no parts were uploaded (i.e., file size might be less than 5 MB)
    if (this.parts.length === 0) {
      // Perform a regular upload instead
      const params = {
        Bucket: this.bucket,
        Key: this.key,
        Body: body,
        ContentType: "audio/webm"
      };

      this.s3.putObject(params, (err, data) => {
        if (err) {
          if (callback) callback(err, null);
          return;
        }
        if (callback) callback(null, data);
      });
      return;
    }

    const sortedParts = this.parts.sort((a, b) => a.PartNumber - b.PartNumber);
    const params = {
      Bucket: this.bucket,
      Key: this.key,
      UploadId: this.uploadId,
      MultipartUpload: { Parts: sortedParts }
    };

    this.s3.completeMultipartUpload(params, (err, data) => {
      if (err) {
        if (callback) callback(err, null);
        return;
      }
      if (callback) callback(null, data);
    });
  }

  async abortMultipartUpload(callback) {
    if (!this.uploadId) {
      if (callback) callback("Upload ID is not set", null);
      return;
    }

    const params = {
      Bucket: this.bucket,
      Key: this.key,
      UploadId: this.uploadId
    };

    this.s3.abortMultipartUpload(params, (err, data) => {
      if (err) {
        if (callback) callback(err, null);
        return;
      }
      if (callback) callback(null, data);
    });
  }
}
